import { useContext, useEffect, useState } from "react";
import { CartContext } from "../context/CartContext";
import { isBMI, getMyAccountId, BMI_CUSTOMER_IDS } from "../utils/config";
import { fetchBudgets, fetchBudgetConsumption } from "../utils/budgetApi";
import { computeCartTotal } from "../utils/buildOrderPayload";
import { API_BASE } from "../utils/api";
import { MONTHS } from "../components/BudgetGrid";

// Asignado vs consumo real del mes + lo que hay en el carrito, para la sucursal
// del cliente BMI logueado. Se recalcula solo al agregar/quitar del carrito, y se
// refresca cuando se aprueba una orden (evento "budget-updated"). Con
// { global: true } (solo Aprobador) suma todas las sucursales del cliente en
// vez de solo la activa, y no proyecta el carrito (pertenece a una sola sucursal).
export default function useBudgetStatus(user, { global = false } = {}) {
    const { cartItems } = useContext(CartContext);
    const [budgetState, setBudgetState] = useState(null);

    useEffect(() => {
        if (!isBMI(user)) {
            setBudgetState(null);
            return;
        }

        const MYACCOUNT_ID = getMyAccountId();
        const currentMonthKey = MONTHS[new Date().getMonth()].key;
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();

        const loadBudgetState = async () => {
            try {
                // Catalogo de categorias FUSIONADO (UIO+GYE), no solo el del cliente
                // logueado — Mantenimiento de Presupuestos asigna presupuesto usando ese
                // mismo catalogo fusionado, asi que una categoria que no esta en el
                // catalogo propio de este cliente (ej. GYE no tiene "SUMINISTROS OFICINA")
                // puede igual tener presupuesto asignado y necesita mostrar su nombre real.
                const catsResults = await Promise.all(
                    BMI_CUSTOMER_IDS.map((idCustomer) =>
                        fetch(
                            `${API_BASE}/inventary-ultra/api/ws/list_categorys?myaccount_id=${MYACCOUNT_ID}&id_customer=${idCustomer}`
                        ).then((r) => r.json())
                    )
                );
                const catsMap = {};
                catsResults.flat().forEach((c) => {
                    if (c && !catsMap[c.categoryId]) catsMap[c.categoryId] = c;
                });
                const cats = Object.values(catsMap);

                // Sucursales a considerar: solo la activa, o todas (vista global del Aprobador).
                // parameters_cust_ship ya trae fusionadas las sedes de los dos clientes BMI
                // (UIO y GYE), cada fila con su propio idCustomer real — asi que en vista
                // global no se puede asumir user.idCustomer para todas, hay que usar el
                // idCustomer real de cada sucursal (Matriz sigue siendo user.idCustomer).
                let idShipTos = [{ idShipTo: user.idShipTo || 0, idCustomer: user.idCustomer }];
                if (global) {
                    const shipTos = await fetch(
                        `${API_BASE}/api-ultra/api/ws/parameters_cust_ship?id_employee=${user.idEmployee}&myaccount_id=${MYACCOUNT_ID}&has_ship_to=true&id_customer=${user.idCustomer}`
                    ).then((r) => r.json());
                    const branches = (Array.isArray(shipTos) ? shipTos : [])
                        .filter((s) => s.idShipTo)
                        .map((s) => ({ idShipTo: s.idShipTo, idCustomer: s.idCustomer }));
                    idShipTos = [{ idShipTo: 0, idCustomer: user.idCustomer }, ...branches];
                }

                const [budgetResults, consumptionResults] = await Promise.all([
                    Promise.all(
                        idShipTos.map(({ idShipTo, idCustomer }) =>
                            fetchBudgets({ myaccountId: MYACCOUNT_ID, idCustomer, idShipTo })
                        )
                    ),
                    Promise.all(
                        idShipTos.map(({ idShipTo, idCustomer }) => fetchBudgetConsumption({ idCustomer, idShipTo }))
                    ),
                ]);

                const asignadoByCategory = {};
                budgetResults.flat().forEach((r) => {
                    asignadoByCategory[r.categoryId] =
                        (asignadoByCategory[r.categoryId] || 0) + (Number(r[currentMonthKey]) || 0);
                });
                const consumoByCategory = {};
                consumptionResults
                    .flat()
                    .filter((r) => r.year1 === currentYear && r.month1 === currentMonth)
                    .forEach((r) => {
                        consumoByCategory[r.categoryId] = (consumoByCategory[r.categoryId] || 0) + Number(r.totalPrice || 0);
                    });

                // El total (asignado y consumoMes) tiene que salir de TODAS las
                // categorias, no solo de las que tienen presupuesto puesto — si no,
                // el consumo real de una categoria sin presupuesto asignado
                // desaparecia del % general (aunque el admin si lo contaba bien en
                // Mantenimiento de Presupuestos). El desglose del panel incluye
                // ademas las categorias sin presupuesto que SI tienen consumo real
                // (para alertarlas), y descarta las que no tienen ni presupuesto ni
                // consumo (no aportan nada que mostrar).
                // Ojo: list_categorys es el catalogo propio del cliente logueado, pero
                // Mantenimiento de Presupuestos usa el catalogo FUSIONADO (UIO+GYE) para
                // asignar — asi que puede haber presupuesto asignado a una categoria que
                // no esta en el catalogo de este cliente puntual (ej. GYE no tiene
                // "SUMINISTROS OFICINA" en su catalogo, pero si tiene budget asignado ahi).
                // Por eso la lista de categorias sale de la UNION de catalogo+asignado+consumo,
                // no solo del catalogo — si no, ese presupuesto asignado desaparece del total.
                const categoryNameById = {};
                (cats || []).forEach((c) => {
                    categoryNameById[c.categoryId] = c.categoryName;
                });
                const allCategoryIds = new Set([
                    ...(cats || []).map((c) => c.categoryId),
                    ...Object.keys(asignadoByCategory),
                    ...Object.keys(consumoByCategory),
                ]);
                const allCategories = Array.from(allCategoryIds).map((categoryId) => ({
                    categoryId,
                    categoryName: categoryNameById[categoryId] || categoryId,
                    asignado: asignadoByCategory[categoryId] || 0,
                    consumo: consumoByCategory[categoryId] || 0,
                }));
                const asignado = allCategories.reduce((sum, c) => sum + c.asignado, 0);
                const consumoMes = allCategories.reduce((sum, c) => sum + c.consumo, 0);
                const categories = allCategories.filter((c) => c.asignado > 0 || c.consumo > 0);
                setBudgetState({ asignado, consumoMes, categories });
            } catch {
                setBudgetState(null);
            }
        };

        loadBudgetState();
        window.addEventListener("budget-updated", loadBudgetState);
        return () => window.removeEventListener("budget-updated", loadBudgetState);
    }, [user?.idCustomer, user?.idShipTo, user?.idEmployee, global]);

    if (!isBMI(user) || !budgetState || budgetState.asignado <= 0) return null;

    // Vista global: solo lectura, sin proyeccion de carrito (el carrito es de una sola sucursal).
    if (global) {
        const pct = (budgetState.consumoMes / budgetState.asignado) * 100;
        const categories = budgetState.categories.map((c) => ({
            ...c,
            pct: c.asignado > 0 ? (c.consumo / c.asignado) * 100 : 0,
        }));
        return { asignado: budgetState.asignado, pct, categories };
    }

    // El consumo real (budgetState.consumoMes/consumo por categoria) viene del ERP
    // ya con IVA incluido, asi que lo que el carrito "proyecta" hacia el presupuesto
    // tiene que calcularse igual (total, no subtotal) para que la comparacion sea
    // consistente — si no, el % se veia mas bajo de lo real mientras habia items
    // gravados en el carrito.
    // El total usa computeCartTotal (la MISMA formula que buildOrderPayload usa al
    // confirmar la orden) para que este gauge y el aviso de Checkout.jsx (que recibe
    // order.total) nunca muestren un % distinto para el mismo carrito.
    const { total: cartTotal } = computeCartTotal(cartItems, user);

    const porcentaje = Number(user?.porcentaje || 0) / 100;
    const itemTotalCents = (item) => {
        const price = item.price ?? item.unitPrice;
        const subtotalCents = Math.round(Math.round(price * 10000) * item.qty / 100);
        const taxCents = item.payTax ? Math.round(subtotalCents * porcentaje) : 0;
        return subtotalCents + taxCents;
    };

    const cartTotalByCategory = {};
    cartItems.forEach((item) => {
        if (!item.categoryId) return;
        const amount = itemTotalCents(item) / 100;
        cartTotalByCategory[item.categoryId] = (cartTotalByCategory[item.categoryId] || 0) + amount;
    });

    const pct = ((budgetState.consumoMes + cartTotal) / budgetState.asignado) * 100;
    const categories = budgetState.categories.map((c) => {
        const consumoProyectado = c.consumo + (cartTotalByCategory[c.categoryId] || 0);
        return {
            ...c,
            consumo: consumoProyectado,
            pct: c.asignado > 0 ? (consumoProyectado / c.asignado) * 100 : 0,
        };
    });

    return { asignado: budgetState.asignado, pct, categories };
}
